import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  type FilterConfig,
  type PaginationState,
  type RTKQueryHook,
} from "../interface/paginatedSelect.interface";

import usePaginatedQuery from "./UseSelectPagination";

import useDebounce from "@/hooks/state/useDebounce";

interface CacheUpdateContext<T> {
  option: T;
  optionValue: string;
  previousCache: Map<string, T>;
}

interface UseAsyncSelectOptionsParams<T, P> {
  baseParams?: P extends void ? never : Omit<P, "filter">;
  filterConfig?: FilterConfig;
  filterFn?: (option: T, query: string) => boolean;
  filterQueryList?: (queryList: T[]) => T[];
  getOptionValue: (option: T) => string;
  initialSearchTerm?: string;
  limit: number;
  loadMoreThreshold: number;
  open: boolean;
  preload?: boolean;
  shouldCacheOption: (context: CacheUpdateContext<T>) => boolean;
  shouldReplaceCachedOption?: (
    existingOption: T | undefined,
    nextOption: T,
  ) => boolean;
  shouldTreatOptionAsChanged?: (
    currentOption: T,
    previousOption: T | undefined,
  ) => boolean;
  useQuery: RTKQueryHook<T, P>;
}

interface AsyncSelectLoadingState {
  showSearchLoader: boolean;
  showMainSkeleton: boolean;
  showLoadMoreIndicator: boolean;
}

interface UseAsyncSelectOptionsResult<T> {
  allOptions: T[];
  debouncedSearchTerm: string;
  displayOptions: T[];
  error: unknown;
  handleScroll: (event: React.UIEvent<HTMLDivElement>) => void;
  hasMore: boolean;
  isFetching: boolean;
  isLoading: boolean;
  loadMore: () => void;
  loadingStates: AsyncSelectLoadingState;
  paginationState: PaginationState;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  searchInputRef: React.RefObject<HTMLInputElement>;
  searchTerm: string;
  selectedOptionsCache: Map<string, T>;
  setAllOptions: React.Dispatch<React.SetStateAction<T[]>>;
  setDisplayOptions: React.Dispatch<React.SetStateAction<T[]>>;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  setSelectedOptionsCache: React.Dispatch<React.SetStateAction<Map<string, T>>>;
  skipQuery: boolean;
}

const shouldReplaceCacheByDefault = <T>(
  existingOption: T | undefined,
): boolean => {
  return !existingOption;
};

const shouldTreatOptionAsChangedByDefault = <T>(
  currentOption: T,
  previousOption: T | undefined,
  getOptionValue: (option: T) => string,
): boolean => {
  if (!previousOption) {
    return true;
  }

  return getOptionValue(currentOption) !== getOptionValue(previousOption);
};

/**
 * Provides option loading, search, cache, and infinite-scroll behavior for async selects.
 * @param params Hook configuration including query contract, mapping logic, and behavior flags.
 * @returns Hook state and handlers for managing async select options.
 */
export function useAsyncSelectOptions<T, P>(
  params: UseAsyncSelectOptionsParams<T, P>,
): UseAsyncSelectOptionsResult<T> {
  const {
    baseParams,
    filterConfig,
    filterFn,
    filterQueryList,
    getOptionValue,
    initialSearchTerm = "",
    limit,
    loadMoreThreshold,
    open,
    preload,
    shouldCacheOption,
    shouldReplaceCachedOption = shouldReplaceCacheByDefault,
    shouldTreatOptionAsChanged,
    useQuery,
  } = params;

  const [allOptions, setAllOptions] = useState<T[]>([]);
  const [displayOptions, setDisplayOptions] = useState<T[]>([]);
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedOptionsCache, setSelectedOptionsCache] = useState<
    Map<string, T>
  >(new Map());

  const prevOptionsRef = useRef<T[]>([]);
  const prevOffsetRef = useRef<number>(0);
  const debouncedSearchTerm = useDebounce(searchTerm, preload ? 0 : 500);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const skipQuery = !open && !preload;

  const normalizedFilterConfig = useMemo(() => {
    return {
      offsetKey: filterConfig?.offsetKey || "offset",
      limitKey: filterConfig?.limitKey || "limit",
      queryKey: filterConfig?.queryKey || "search",
      queryParamsParamKey: filterConfig?.queryParamsParamKey || "filter",
      customBuilder: filterConfig?.customBuilder,
    };
  }, [filterConfig]);

  const {
    error,
    isLoading,
    isFetching,
    paginationState,
    setLimit,
    setQuery,
    loadMore,
    hasMore,
    data,
  } = usePaginatedQuery(
    useQuery,
    baseParams,
    normalizedFilterConfig,
    skipQuery,
  );

  useEffect(() => {
    if (paginationState.limit !== limit) {
      setLimit(limit);
    }
  }, [limit, paginationState.limit, setLimit]);

  useEffect(() => {
    if (skipQuery) {
      return;
    }

    setQuery(debouncedSearchTerm || undefined);
    setIsLoadingMore(false);
  }, [debouncedSearchTerm, setQuery, skipQuery]);

  const options = useMemo(() => data?.data ?? [], [data?.data]);

  useEffect(() => {
    if (!skipQuery && options.length > 0) {
      const optionsChanged =
        options.length !== prevOptionsRef.current.length ||
        options.some((option, index) => {
          const previousOption = prevOptionsRef.current[index];

          return shouldTreatOptionAsChanged
            ? shouldTreatOptionAsChanged(option, previousOption)
            : shouldTreatOptionAsChangedByDefault(
                option,
                previousOption,
                getOptionValue,
              );
        });

      const offsetChanged = paginationState.offset !== prevOffsetRef.current;

      if (optionsChanged || offsetChanged) {
        setSelectedOptionsCache((previousCache) => {
          const nextCache = new Map(previousCache);
          let didUpdateCache = false;

          options.forEach((option) => {
            const optionValue = getOptionValue(option);

            if (!optionValue) {
              return;
            }

            if (!shouldCacheOption({ option, optionValue, previousCache })) {
              return;
            }

            const existingOption = previousCache.get(optionValue);

            if (!shouldReplaceCachedOption(existingOption, option)) {
              return;
            }

            nextCache.set(optionValue, option);
            didUpdateCache = true;
          });

          return didUpdateCache ? nextCache : previousCache;
        });

        if (paginationState.offset === 0) {
          setAllOptions(options);
        } else {
          setAllOptions((previousOptions) => {
            const newOptions = options.filter((nextOption) => {
              return !previousOptions.some((existingOption) => {
                return (
                  getOptionValue(existingOption) === getOptionValue(nextOption)
                );
              });
            });

            return [...previousOptions, ...newOptions];
          });
        }

        prevOptionsRef.current = options;
        prevOffsetRef.current = paginationState.offset;
      }

      setIsLoadingMore(false);

      return;
    }

    if (!skipQuery && paginationState.offset === 0) {
      setAllOptions([]);
      prevOptionsRef.current = [];
      prevOffsetRef.current = 0;
    }
  }, [
    getOptionValue,
    options,
    paginationState.offset,
    shouldCacheOption,
    shouldReplaceCachedOption,
    shouldTreatOptionAsChanged,
    skipQuery,
  ]);

  useEffect(() => {
    let filteredOptions = allOptions;

    if (preload && debouncedSearchTerm && filterFn) {
      filteredOptions = allOptions.filter((option) => {
        return filterFn(option, debouncedSearchTerm);
      });
    }

    if (filterQueryList) {
      filteredOptions = filterQueryList(filteredOptions);
    }

    setDisplayOptions([...filteredOptions]);
  }, [allOptions, debouncedSearchTerm, filterFn, filterQueryList, preload]);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (preload || !hasMore || isFetching || isLoading || isLoadingMore) {
        return;
      }

      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      const isNearBottom =
        scrollHeight - scrollTop - clientHeight < loadMoreThreshold;

      if (isNearBottom) {
        setIsLoadingMore(true);
        loadMore();
      }
    },
    [
      hasMore,
      isFetching,
      isLoading,
      isLoadingMore,
      loadMore,
      loadMoreThreshold,
      preload,
    ],
  );

  const loadingStates = useMemo(() => {
    const isFirstLoad = paginationState.offset === 0;
    const hasSearchQuery = searchTerm.trim().length > 0;

    return {
      showSearchLoader: hasSearchQuery && isFetching && !isLoadingMore,
      showMainSkeleton:
        (isLoading || (isFetching && isFirstLoad && !isLoadingMore)) &&
        !preload &&
        !searchTerm,
      showLoadMoreIndicator:
        isFetching && isLoadingMore && hasMore && displayOptions.length > 0,
    };
  }, [
    displayOptions.length,
    hasMore,
    isFetching,
    isLoading,
    isLoadingMore,
    paginationState.offset,
    preload,
    searchTerm,
  ]);

  return {
    allOptions,
    debouncedSearchTerm,
    displayOptions,
    error,
    handleScroll,
    hasMore,
    isFetching,
    isLoading,
    loadMore,
    loadingStates,
    paginationState,
    scrollContainerRef,
    searchInputRef,
    searchTerm,
    selectedOptionsCache,
    setAllOptions,
    setDisplayOptions,
    setSearchTerm,
    setSelectedOptionsCache,
    skipQuery,
  };
}
